/**
 * The live run card shown above the composer while a workflow executes
 * (and after, until dismissed): step checklist with the current step
 * pulsing, per-step status marks, elapsed running time, and the latest
 * completed step's output excerpt so intermediate results are visible as
 * they land. Data comes from `useWorkflowRunProgress` (stream-fed run
 * record, poll fallback); status colors match the Workflow Details page.
 */

import { useEffect, useState } from 'react';
import { formatElapsed } from './formatElapsed.ts';
import type { WorkflowRunProgress as Progress } from './useWorkflowRunProgress.ts';
import './WorkflowRunProgress.css';

const STATUS_LABEL: Record<string, string> = {
  running: 'RUNNING',
  succeeded: 'DONE',
  failed: 'FAILED',
  'limit-stopped': 'PAUSED — DAILY LIMIT',
  aborted: 'STOPPED',
};

/** Elapsed run time in ms, or null when it can't be derived. While running,
 *  prefers the stream's server-authoritative anchor (immune to runtime↔browser
 *  clock skew); poll-sourced progress falls back to `initiatedAt`. Terminal
 *  records show the static total. */
function elapsedOf(progress: Progress, now: number): number | null {
  const { record, elapsedBase } = progress;
  if (record.status === 'running') {
    if (elapsedBase !== undefined) {
      return elapsedBase.ms + (now - elapsedBase.at);
    }
    const started = Date.parse(record.initiatedAt);
    return Number.isNaN(started) ? null : now - started;
  }
  if (record.completedAt === undefined) {
    return null;
  }
  const started = Date.parse(record.initiatedAt);
  const ended = Date.parse(record.completedAt);
  return Number.isNaN(started) || Number.isNaN(ended) ? null : ended - started;
}

function stepMark(status: string, isCurrent: boolean): string {
  if (isCurrent) {
    return '▸';
  }
  switch (status) {
    case 'succeeded':
      return '✓';
    case 'failed':
      return '✗';
    case 'limit-stopped':
      return '⏸';
    default:
      return '·';
  }
}

export default function WorkflowRunProgress({
  progress,
  onDismiss,
}: {
  progress: Progress;
  onDismiss: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const { record, displayName } = progress;
  const running = record.status === 'running';
  // 1s re-render while running so the elapsed readout ticks between the
  // stream's ~30s updates; terminal cards are static.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) {
      return;
    }
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [running]);
  const elapsed = elapsedOf(progress, now);
  // The step being worked on right now: first non-terminal one.
  const currentIndex = running
    ? record.steps.find((s) => s.status !== 'succeeded' && s.status !== 'failed')?.index
    : undefined;
  const done = record.steps.filter((s) => s.status === 'succeeded').length;
  // Latest intermediate result — the most recent completed step with output.
  const latest = [...record.steps]
    .reverse()
    .find((s) => s.status === 'succeeded' && s.output !== undefined && s.output.length > 0);

  return (
    <section
      className={`om-wfrun${running ? ' om-wfrun--live' : ''}`}
      aria-label="Workflow run progress"
      data-status={record.status}
    >
      <div className="om-wfrun__head">
        <span className="om-wfrun__glyph" aria-hidden="true">
          ⛁
        </span>
        <span className="om-wfrun__name">{displayName}</span>
        <span className="om-wfrun__count">
          {done}/{record.steps.length} steps
        </span>
        {elapsed !== null ? (
          <span className="om-wfrun__elapsed" title="Run time">
            {formatElapsed(elapsed)}
          </span>
        ) : null}
        <span className={`om-wfrun__status om-wfrun__status--${record.status}`}>
          {STATUS_LABEL[record.status] ?? record.status.toUpperCase()}
        </span>
        <button
          type="button"
          className="om-wfrun__close"
          aria-label="Dismiss run progress"
          onClick={onDismiss}
        >
          ✕
        </button>
      </div>
      <ol className="om-wfrun__steps">
        {record.steps.map((s) => {
          const isCurrent = s.index === currentIndex;
          return (
            <li
              key={s.index}
              className={`om-wfrun__step om-wfrun__step--${s.status}${
                isCurrent ? ' om-wfrun__step--current' : ''
              }`}
            >
              <span className="om-wfrun__mark" aria-hidden="true">
                {stepMark(s.status, isCurrent)}
              </span>
              <span className="om-wfrun__title">{s.title}</span>
              {s.error !== undefined ? (
                <span className="om-wfrun__error">{s.error}</span>
              ) : null}
            </li>
          );
        })}
      </ol>
      {latest !== undefined ? (
        <button
          type="button"
          className="om-wfrun__excerpt"
          data-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          title={expanded ? 'Collapse' : 'Expand'}
        >
          <span className="om-wfrun__excerpt-label">
            Step {latest.index} · {latest.title}
          </span>
          <span className="om-wfrun__excerpt-text">{latest.output}</span>
        </button>
      ) : null}
    </section>
  );
}
